/**
 * Overrides for content that lives in Supabase / Firebase (AI recommended
 * topics and question subsection names). The backend only stores English
 * strings, so we key these maps on the English source text and look up the
 * localized version at render time. Entries that don't match fall back to
 * the original string.
 *
 * Keep keys aligned exactly (including punctuation) with the backend strings.
 */

import i18n, { getCurrentLanguage } from "./index";

type StringMap = Record<string, string>;

// AI recommended topics (backend: lessons collection)
// Keys are the English `title` / `description` values returned by the backend.
const AI_TOPIC_TITLE_ES: StringMap = {
  "⚖️ Neutral Conductor Sizing": "⚖️ Dimensionamiento del conductor neutro",
  "🏠 Service Size Calculations": "🏠 Cálculos del tamaño del servicio",
  "🔥 Heating & AC Calculations for Services":
    "🔥 Cálculos de calefacción y aire acondicionado para servicios",
  "🔥 Conductor Sizing": "🔥 Dimensionamiento del conductor",
  "Conductor Sizing": "Dimensionamiento del conductor",
  "🔌 Appliance Load Calculations":
    "🔌 Cálculos de carga de electrodomésticos",
  "💡 Lighting Load Calculations": "💡 Cálculos de carga de iluminación",
  "Motor Feeder Sizing": "Dimensionamiento de alimentadores de motor",
  "Motor Branch Circuit Sizing":
    "Dimensionamiento de circuitos derivados de motor",
  "Motor Overload Protection": "Protección contra sobrecarga de motores",
  "Motor Short Circuit Protection":
    "Protección contra cortocircuitos de motores",
  "Conductor Ampacity": "Ampacidad de conductores",
  "Conduit Fill": "Llenado de conductos",
  "Box Fill Calculations": "Cálculos de llenado de cajas",
  "Voltage Drop": "Caída de voltaje",
  Grounding: "Puesta a tierra",
  Bonding: "Unión equipotencial",
  "Transformer Sizing": "Dimensionamiento de transformadores",
  "GFCI & AFCI Requirements": "Requisitos de GFCI y AFCI",
  "Special Occupancies": "Ocupaciones especiales",
  "Hazardous Locations": "Ubicaciones peligrosas",
};

const AI_TOPIC_DESCRIPTION_ES: StringMap = {
  "Learn how to size the neutral conductor based on unbalanced loads.":
    "Aprende a dimensionar el conductor neutro en función de cargas desequilibradas.",
  "Determine the correct size of electrical service for a building.":
    "Determina el tamaño correcto del servicio eléctrico para un edificio.",
  "Calculate heating and air conditioning loads when sizing a electrical service.":
    "Calcula las cargas de calefacción y aire acondicionado al dimensionar un servicio eléctrico.",
  "Understand how to factor appliances into your overall load calculation.":
    "Entiende cómo incluir los electrodomésticos en el cálculo general de carga.",
  "Learn how to calculate lighting loads for residential and commercial spaces.":
    "Aprende a calcular las cargas de iluminación para espacios residenciales y comerciales.",
};

// Subsection names (backend: subsection_names / US_subsection_names)
const SUBSECTION_NAME_ES: StringMap = {
  "Services & Load Calcs": "Servicios y cálculos de carga",
  Services: "Servicios",
  "Load Calculations": "Cálculos de carga",
  Transformers: "Transformadores",
  Grounding: "Puesta a tierra",
  Bonding: "Unión equipotencial",
  "Grounding & Bonding": "Puesta a tierra y unión",
  "Branch Circuits": "Circuitos derivados",
  "Conductor Sizing": "Dimensionamiento de conductores",
  "Conduit Sizing": "Dimensionamiento de conductos",
  "Box Fill": "Llenado de cajas",
  Motors: "Motores",
  "Motor Controls": "Controles de motores",
  "Motor Sizing": "Dimensionamiento de motores",
  HVAC: "HVAC",
  "Special Systems": "Sistemas especiales",
  "Communication Systems": "Sistemas de comunicación",
  "Fire Alarm Systems": "Sistemas de alarma contra incendios",
  "Hazardous Locations": "Ubicaciones peligrosas",
  "Hazardous Areas": "Áreas peligrosas",
  "Safety & Tools": "Seguridad y herramientas",
  "Safety Functions": "Funciones de seguridad",
  "Trade Knowledge": "Conocimientos del oficio",
  "Support Systems": "Sistemas de apoyo",
  "Code Lookups": "Búsquedas en el código",
  Calculations: "Cálculos",
  Definitions: "Definiciones",
  Wiring: "Cableado",
  "Wiring Methods": "Métodos de cableado",
  "Raceways and Cables": "Canalizaciones y cables",
  Lighting: "Iluminación",
  "Lighting Loads": "Cargas de iluminación",
  "Lighting Systems": "Sistemas de iluminación",
  "Heating Systems": "Sistemas de calefacción",
  "Heating & AC": "Calefacción y aire acondicionado",
  "Voltage Drop": "Caída de voltaje",
  "Overcurrent Protection": "Protección contra sobrecorriente",
  "High Voltage": "Alta tensión",
  "High Voltage Systems": "Sistemas de alta tensión",
  "Power Generation": "Generación de energía",
  "Renewable Energy": "Energía renovable",
  "Consumer / Supply Systems": "Sistemas de consumo / suministro",
  "Protection Devices": "Dispositivos de protección",
  "Distribution Systems": "Sistemas de distribución",
  "Grounding Systems": "Sistemas de puesta a tierra",
  UPS: "SAI",
  Meters: "Medidores",
};

function lookup(map: StringMap, value: string | undefined | null): string {
  if (!value) return "";
  if (getCurrentLanguage() !== "es") return value;
  return map[value] ?? value;
}

const EMOJI_TOKEN_SOURCE = String.raw`(?:\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?)*)`;
const EMOJI_RE = new RegExp(EMOJI_TOKEN_SOURCE, "gu");
const FIRST_EMOJI_RE = new RegExp(EMOJI_TOKEN_SOURCE, "u");

export function stripEmojis(value: string): string {
  return value.replace(EMOJI_RE, " ").replace(/\s+/g, " ").trim();
}

export function getFirstEmoji(value: string | undefined | null): string | undefined {
  return value?.match(FIRST_EMOJI_RE)?.[0];
}

// Resolution order for the helpers below when language is `es`:
//   1. Firebase sibling field (`titleEs` / `descriptionEs` / `nameEs`) if
//      passed in.
//   2. Legacy override map — fallback during transition so untranslated
//      Firebase docs still surface Spanish strings the maps already cover.
//   3. English source string.
// When language is `en`, always returns the English source.

export function localizeAiTopicTitle(
  title: string | undefined | null,
  titleEs?: string | null,
): string {
  if (getCurrentLanguage() === "es" && titleEs) return titleEs;
  return lookup(AI_TOPIC_TITLE_ES, title);
}

// Same as localizeAiTopicTitle but without emoji.
export function localizeAiTopicTitlePlain(
  title: string | undefined | null,
  titleEs?: string | null,
): string {
  return stripEmojis(localizeAiTopicTitle(title, titleEs));
}

// Every plain rendering localizeAiTopicTitlePlain could have produced for this
// topic, in any supported language, regardless of which one is active now.
//
// A resumed chat's opener was generated in a past session, possibly under a
// different language setting, so the single current-language rendering is not
// enough to recognise it. Callers cross this with the localized template to
// rebuild the set of texts the opener may have taken.
//
// Mirrors blue-collar-app/src/i18n/serverContentCopy.ts.
export function aiTopicTitlePlainVariants(
  title: string | undefined | null,
  titleEs?: string | null,
): string[] {
  const variants = [
    title,
    titleEs,
    // The legacy override map, read directly rather than through `lookup` —
    // that helper short-circuits to English whenever the active language is not
    // Spanish, which is exactly the case this function has to see past.
    title ? AI_TOPIC_TITLE_ES[title] : null,
  ];

  const seen = new Set<string>();
  for (const variant of variants) {
    const plain = variant ? stripEmojis(variant) : "";
    if (plain) seen.add(plain);
  }
  return [...seen];
}

export function localizeAiTopicDescription(
  description: string | undefined | null,
  descriptionEs?: string | null,
): string {
  if (getCurrentLanguage() === "es" && descriptionEs) return descriptionEs;
  return lookup(AI_TOPIC_DESCRIPTION_ES, description);
}

export function localizeSubsectionName(
  name: string | undefined | null,
  nameEs?: string | null,
): string {
  if (getCurrentLanguage() === "es" && nameEs) return nameEs;
  return lookup(SUBSECTION_NAME_ES, name);
}

// Backend serves state descriptions as a single English sentence per state
// (see BACKUP_STATE_INFO_US.stateDescriptions). The boilerplate follows a
// fixed pattern, so for Spanish we parse out year/exam name/state name and
// re-render via an i18n template; the boilerplate translates while the
// proper-noun-ish exam/state names stay in English. Falls back to the
// English (year-substituted) string if the regex doesn't match.
//
// The trailing "Curriculum inclusive of journeyman, master, or contractor
// electrical exams." sentence is captured optionally — the live backend
// includes it for some states (California, Texas) and omits it for others
// (Alabama). The state-name group uses `.+?` (not `[^.]+?`) so names that
// legitimately contain a period (e.g. "St. Louis") still parse; the literal
// "Curriculum inclusive" anchor in the optional group disambiguates which
// trailing period belongs to the state vs the boilerplate.
const STATE_DESC_RE =
  /^Based on (\d{4}) National Electrical Code for tradepeople taking the (.+?) exam in (.+?)(\.\s*Curriculum inclusive of journeyman, master, or contractor electrical exams\.?)?\.?$/;

export function localizeStateDescription(
  description: string | undefined | null,
  displayYear?: number | string | null,
): string {
  if (!description) return "";
  const withYear = displayYear
    ? description.replace(/\b\d{4}\b/, String(displayYear))
    : description;
  if (getCurrentLanguage() !== "es") return withYear;
  const m = withYear.match(STATE_DESC_RE);
  if (!m) return withYear;
  const [, year, examName, stateName, curriculum] = m;
  const key = curriculum
    ? "examType.state_description_template"
    : "examType.state_description_template_short";
  return i18n.t(key, { year, examName, stateName });
}
